import React from 'react';
import { cn } from '../lib/utils';

export interface NavbarProps extends React.HTMLAttributes<HTMLElement> {
  logo?: React.ReactNode;
  sticky?: boolean;
}

export const Navbar = React.forwardRef<HTMLElement, NavbarProps>(
  ({ className, logo, sticky = true, children, ...props }, ref) => {
    return (
      <nav
        ref={ref}
        className={cn(
          'border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900',
          sticky && 'sticky top-0 z-40',
          className
        )}
        {...props}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {logo && <div className="flex-shrink-0">{logo}</div>}
          <div className="flex flex-1 items-center justify-between">
            {children}
          </div>
        </div>
      </nav>
    );
  }
);

Navbar.displayName = 'Navbar';

export const NavbarContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-6', className)}
    {...props}
  />
));

NavbarContent.displayName = 'NavbarContent';

export const NavbarMenu = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('hidden items-center gap-6 md:flex', className)}
    {...props}
  />
));

NavbarMenu.displayName = 'NavbarMenu';

export const NavbarItem = React.forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { active?: boolean }
>(({ className, active, ...props }, ref) => (
  <a
    ref={ref}
    className={cn(
      'text-sm font-medium transition-colors hover:text-brand-500',
      active
        ? 'text-brand-500'
        : 'text-gray-700 dark:text-gray-300',
      className
    )}
    {...props}
  />
));

NavbarItem.displayName = 'NavbarItem';

export const NavbarActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-4', className)}
    {...props}
  />
));

NavbarActions.displayName = 'NavbarActions';
